import {
  FeatureFlags,
  getCommercialDefaultFlags,
  getGovDefaultFlags,
} from '@strength/utils';
import axios from 'axios';
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { AuthContext } from '../../auth';
import { environment } from '../../environment';

type FeatureFlagsContextType = FeatureFlags;

const FeatureFlagsContext = createContext<FeatureFlagsContextType | null>(null);

export const FeatureFlagsProvider = ({
  children,
  authContext,
}: PropsWithChildren & { authContext: AuthContext }) => {
  const [flags, setFlags] = useState<FeatureFlags>(
    environment.VITE_ENV_TYPE === 'il4'
      ? getGovDefaultFlags(environment.VITE_ENVIRONMENT)
      : getCommercialDefaultFlags(),
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchFlags = async () => {
      if (!authContext.auth) {
        setIsLoading(false);
        return;
      }
      
      try {
        const res = await axios.get(`${environment.VITE_API_URL}/flags`, {
          withCredentials: true,
        });
        setFlags(res.data);
      } catch (error) {
        console.error('Failed to fetch feature flags:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchFlags();
  }, [authContext]);

  if (isLoading) {
    return null;
  }

  return (
    <FeatureFlagsContext.Provider value={flags}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFlags = (): FeatureFlags => {
  const ctx = useContext(FeatureFlagsContext);

  if (!ctx) {
    throw new Error('useFlags must be used within a FeatureFlagsProvider');
  }

  return ctx;
};
